import { useState } from 'react'
import React from 'react'

import { Box, Text, Title, useOnNext } from '@factorialco/deprecated-design-system'
import { Card, TogglerCheckbox } from '@factorialco/deprecated-design-system'
import { RichTextEditor, RichTextEditorHandle } from '@factorialco/f0-react/dist/experimental'
import { FormikErrors } from 'formik'
import { CompetencyFragment } from 'generated/resources/graphql'

import Callout from 'modules/core/components/Callout'
import HelpCenterLink from 'modules/core/components/HelpCenterLink'
import Selector from 'modules/core/components/Selector'
import TextEditor, { type EditorHandle } from 'modules/core/components/TextEditor'
import TextField from 'modules/core/components/TextField'
import { useEffect } from 'modules/core/hooks/useEffect'
import useRichTextEditor from 'modules/core/hooks/useRichTextEditor'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import DirectUploader from 'modules/trainings/components/Files/Uploaders/DirectUploader'
import { sumMinutesToHours } from 'modules/trainings/components/MyTrainings/RequestsComponent/RequestDetailComponent/useHoursAndMinutes'
import NumberField from 'modules/trainings/components/NumberField'
import { CourseValidity } from 'modules/trainings/components/Revamp/CourseValidity'
import {
  LearningPlatformData,
  TrainingWizardData,
} from 'modules/trainings/components/Revamp/NewTrainingWizard/types'

interface Props {
  data: TrainingWizardData
  onDataChanged: (data: TrainingWizardData) => void
  competencies: CompetencyFragment[]
  onChangeLearningPlatform: (data: LearningPlatformData) => void
  learningPlatformData: LearningPlatformData
  errors: FormikErrors<LearningPlatformData>
  onSubmitLP: () => void
}
export const BasicInfoStep = ({
  data,
  onDataChanged,
  competencies,
  errors,
  learningPlatformData,
  onChangeLearningPlatform,
  onSubmitLP,
}: Props) => {
  const objectivesRichTextEditorRef = React.useRef<RichTextEditorHandle>(null)
  const objectivesTextEditorRef = React.useRef<EditorHandle>(null)
  const descriptionRichTextEditorRef = React.useRef<RichTextEditorHandle>(null)
  const descriptionTextEditorRef = React.useRef<EditorHandle>(null)
  const { editorConfig, canUseRichTextEditor, loading: richTextEditorLoading } = useRichTextEditor()

  const [openLP, setOpenLP] = useState(learningPlatformData.hasLearningPlatform)
  const [openCV, setOpenCV] = useState(false)

  useEffect(() => {
    onChangeLearningPlatform({ ...learningPlatformData, hasLearningPlatform: openLP })
    onSubmitLP()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openLP])

  useOnNext(() => {
    if (openLP) {
      onSubmitLP()
    }
  })

  useOnNext(() => {
    track(trackingEvents.training.trainingCreation.next, {
      has_learning_platform: learningPlatformData.hasLearningPlatform,
    })
    track(trackingEvents.training.trainingCreationWizard.goToBasicInfoNextStep)
  })

  const [hours, setHours] = useState((): number => {
    const duration_hours = data.totalDuration ? data.totalDuration : 0
    return Math.floor(duration_hours)
  })
  const [minutes, setMinutes] = useState((): number => {
    const duration_minutes = data.totalDuration ? data.totalDuration : 0
    return Math.round((duration_minutes % 1) * 60)
  })

  const handleDurationChange = (newHours: number, newMinutes: number) => {
    const durationInHours = sumMinutesToHours(newHours, newMinutes)
    onDataChanged({ ...data, totalDuration: durationInHours })
    setHours(newHours)
    setMinutes(newMinutes)
  }

  return (
    <Box flexDirection="row" gap="s24">
      <Box gap="s24" paddingX="s16">
        <Title
          variant="500"
          title={i18n.t(
            'trainings.trainings_revamp.new_training.wizard.basic_information.step_title'
          )}
          description={i18n.t(
            'trainings.trainings_revamp.new_training.wizard.basic_information.new_step_description'
          )}
        />
        <Box gap="s16">
          <TextField
            label={i18n.t(
              'trainings.trainings_revamp.new_training.wizard.basic_information.new_name'
            )}
            value={data.name}
            onChange={(value) => onDataChanged({ ...data, name: value })}
            mandatory
          />
          <>
            <Box paddingBottom="s8" gap="s4">
              <Text.Normal size="200" weight="medium" color="grey1000">
                {i18n.t('trainings.edit_mode.basic_information.thumbnail_title')}
              </Text.Normal>
              <Text.Normal size="200" weight="normal" color="grey800">
                {i18n.t('trainings.edit_mode.basic_information.thumbnail_description')}
              </Text.Normal>
            </Box>
            <DirectUploader
              onUpload={async (uploadData) => {
                onDataChanged({
                  ...data,
                  thumbnail: uploadData.file,
                })
              }}
              onDelete={() => {
                onDataChanged({ ...data, thumbnail: undefined })
              }}
              description={i18n.t('trainings.edit_mode.basic_information.thumbnail_placeholder')}
              fileTypes={['image' as const]}
              resourceType={'Trainings::Training'}
            />
          </>
          <Box paddingBottom="s8" gap="s4">
            <Text.Normal size="200" weight="medium" color="grey1000">
              {i18n.t('trainings.edit_mode.basic_information.objectives.title')}
            </Text.Normal>
            <Text.Normal size="200" weight="normal" color="grey800">
              {i18n.t('trainings.edit_mode.basic_information.objectives.description')}
            </Text.Normal>
            {richTextEditorLoading ? (
              <RichTextEditor.Skeleton />
            ) : canUseRichTextEditor ? (
              <RichTextEditor
                ref={objectivesRichTextEditorRef}
                {...editorConfig}
                onChange={(value) => onDataChanged({ ...data, objectives: value?.value ?? '' })}
                placeholder=""
                initialEditorState={{ content: data.objectives || '' }}
                title={i18n.t('trainings.edit_mode.basic_information.objectives.title')}
                height="sm"
              />
            ) : (
              <TextEditor
                ref={objectivesTextEditorRef}
                readonly={false}
                placeholder=""
                height="s200"
                initialEditorState={{
                  data: data.objectives,
                }}
                onChange={(value) => onDataChanged({ ...data, objectives: String(value ?? '') })}
              />
            )}
          </Box>
          <Box gap="s4">
            <Text.Normal size="200" weight="medium" color="grey1000">
              {i18n.t('trainings.edit_mode.basic_information.description_title')}
            </Text.Normal>
            <Text.Normal size="200" weight="normal" color="textSecondary">
              {i18n.t('trainings.edit_mode.basic_information.description_description')}
            </Text.Normal>
            {richTextEditorLoading ? (
              <RichTextEditor.Skeleton />
            ) : canUseRichTextEditor ? (
              <RichTextEditor
                ref={descriptionRichTextEditorRef}
                {...editorConfig}
                onChange={(value) => onDataChanged({ ...data, description: value?.value ?? '' })}
                placeholder=""
                initialEditorState={{ content: data.description || '' }}
                title={i18n.t('trainings.edit_mode.basic_information.description_title')}
                height="sm"
              />
            ) : (
              <TextEditor
                ref={descriptionTextEditorRef}
                readonly={false}
                placeholder=""
                height="s200"
                initialEditorState={{
                  data: data.description,
                }}
                onChange={(value) => onDataChanged({ ...data, description: String(value ?? '') })}
              />
            )}
          </Box>
          <Selector.Popover
            label={i18n.t(
              'trainings.trainings_revamp.new_training.wizard.basic_information.competencies'
            )}
            blankLabel={i18n.t(
              'trainings.trainings_revamp.new_training.wizard.basic_information.competencies_blank'
            )}
            placeholder={i18n.t(
              'trainings.trainings_revamp.new_training.wizard.basic_information.competencies_placeholder'
            )}
            isMultiSelect
            description={i18n.t(
              'trainings.trainings_revamp.new_training.wizard.basic_information.competencies_description'
            )}
            value={data.competencyIds || []}
            onChange={(ids) => {
              // eslint-disable-next-line no-type-assertion/no-type-assertion
              onDataChanged({ ...data, competencyIds: ids as number[] })
            }}
            inputStyle="tags"
          >
            {competencies.map(({ id, name }) => (
              <Selector.Option key={id} value={id} label={name} searchValue={name}>
                <Box flexDirection="row" gap="s8" alignItems="center">
                  <Text.Normal>{name}</Text.Normal>
                </Box>
              </Selector.Option>
            ))}
          </Selector.Popover>

          <Box gap="s4" flexDirection="column">
            <Text.Normal size="200" weight="medium" color="grey1000">
              {i18n.t(
                'trainings.trainings_revamp.new_training.wizard.basic_information.total_course_duration'
              )}
            </Text.Normal>
            <Box flexDirection="row" gap="s16">
              <Box gap="s4" flexDirection="column">
                <Text.Normal size="200" weight="normal" color="grey800">
                  {i18n.t('trainings.trainings_revamp.new_training.wizard.basic_information.hours')}
                </Text.Normal>
                <NumberField
                  step={1}
                  min={0}
                  value={hours}
                  onChange={(value) => {
                    handleDurationChange(value ?? 0, minutes)
                  }}
                />
              </Box>
              <Box gap="s4" flexDirection="column">
                <Text.Normal size="200" weight="normal" color="grey800">
                  {i18n.t(
                    'trainings.trainings_revamp.new_training.wizard.basic_information.minutes'
                  )}
                </Text.Normal>
                <NumberField
                  step={1}
                  min={0}
                  max={59}
                  maxDecimals={0}
                  value={minutes}
                  onChange={(value) => {
                    handleDurationChange(hours, value ?? 0)
                  }}
                />
              </Box>
            </Box>
          </Box>

          <Callout
            compact
            type="info"
            description={i18n.t(
              'trainings.trainings_revamp.new_training.wizard.basic_information.competencies_callout'
            )}
            link={
              <HelpCenterLink name={'competencies_goals'} type="solid">
                <Text.Normal size="100" weight="medium" underlined color="grey1000">
                  {i18n.t('common.learn_more')}
                </Text.Normal>
              </HelpCenterLink>
            }
          />
          <Card>
            <Card.Title
              size="small"
              action={
                <TogglerCheckbox
                  value={data.isMandatory ?? false}
                  onChange={(value) => onDataChanged({ ...data, isMandatory: value })}
                />
              }
            >
              {i18n.t(
                'trainings.trainings_revamp.new_training.wizard.basic_information.mandatory_training.title'
              )}
            </Card.Title>
            <Card.Description>
              {i18n.t(
                'trainings.trainings_revamp.new_training.wizard.basic_information.mandatory_training.description'
              )}
            </Card.Description>
          </Card>
          <CourseValidity
            open={openCV}
            setOpen={setOpenCV}
            data={data}
            onChange={({ validFor }) => onDataChanged({ ...data, validFor: validFor })}
          />
        </Box>
      </Box>
    </Box>
  )
}
